import type { JSX } from 'react';
import { useState } from 'react';
import type { WireUser } from '@logistics/contracts';
import { cn } from '@/lib/utils';
import { button, formError } from '../shell/look';
import { Modal } from '../shell/modal';
import { toSourceAccess } from './source-access';
import type { SourceMode } from './source-access';
import { SourceAccessPicker } from './source-access-picker';
import { useChangeUser } from './use-users';

/** Changing which sources an existing person may read. */
export function SourceAccessModal({
  user,
  onClose,
}: {
  user: WireUser;
  onClose: () => void;
}): JSX.Element {
  const [mode, setMode] = useState<SourceMode>(user.sourceAccess === undefined ? 'all' : 'only');
  const [ids, setIds] = useState<string[]>([...(user.sourceAccess ?? [])]);
  const change = useChangeUser();
  const empty = mode === 'only' && ids.length === 0;

  const save = (): void => {
    const sourceAccess = toSourceAccess(mode, ids);
    change.mutate(
      {
        user,
        patch: { sourceAccess },
        done:
          sourceAccess === null
            ? `${user.name} can read every source`
            : `${user.name} can read ${sourceAccess.length} source(s)`,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal eyebrow="ACCESS CONTROL" title={`Sources for ${user.name}`} onClose={onClose}>
      <div className="flex flex-col gap-3.5 overflow-y-auto px-7 py-6">
        <SourceAccessPicker mode={mode} sourceIds={ids} onMode={setMode} onSourceIds={setIds} />
        {empty && (
          <div role="alert" className={formError}>
            Pick at least one source, or allow all sources.
          </div>
        )}
        <p className="text-mute m-0 text-xs leading-normal">
          Applies to chat, the Telegram bot and scheduled answers. &ldquo;All sources&rdquo;
          includes accounts connected later.
        </p>
        <div className="flex justify-end gap-2.5 pt-1.5">
          <button type="button" onClick={onClose} className={button.outline}>
            Cancel
          </button>
          <button
            type="button"
            disabled={empty || change.isPending}
            onClick={save}
            className={cn(button.primary, 'px-[18px]')}
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
}
